<template>
    <div id="contents" class="contents spirit-of-japan">
        <JsonLd :json="jsonld" />
        <section class="main">
            <div class="texts">
                <div class="inner">
                    <p class="sub">A Journey to the True Spirit of Japan</p>
                    <h2>Sacred Sites where Nature and Faith Blend into One</h2>
                    <p class="lead">
                        Since ancient times, the Japanese people have believed that deities dwell in the mountains, and revered the nation’s abundant nature and awe-inspiring landscapes. This belief still holds true today, and Japan’s pristine natural beauty lives on in the countless lakes, mountains,
                        sacred sites and spiritual traditions that remain deeply ingrained in Japan’s cultural legacy. Explore firsthand the untold stories behind the age-old beliefs, history and culture shaped by profound respect for the natural world.
                    </p>
                </div>
            </div>
            <div class="img"><img src="../../assets/spirit_of_japan2/main01.jpg" alt="" class="_pc"><img src="../../assets/spirit_of_japan2/main01_sp.jpg" alt="" class="_sp"></div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan2/img01.jpg" alt="" class="_pc"><img src="../../assets/spirit_of_japan2/img01_sp.jpg" alt="" class="_sp"></div>
                <div class="txt">
                    <h3>Three Peaks where Mountains and Faith are Inextricably Linked</h3>
                    <p>
                        The Three Sacred Mountains of Dewa, collectively called Dewa Sanzan, form the core of the ascetic religion known as Shugendo, a Japanese mountain faith that mixes nature worship with esoteric Buddhist beliefs and grueling training to gain enlightenment.
                        The untouched natural beauty and spiritual wonder of Dewa Sanzan has for centuries inspired the belief that a visit there was a journey to rebirth. The path to the top of Mt. Haguro consists of 2,446 stone steps that pass through towering cedars up to 500 years old,
                        and the simple yet dignified five-story pagoda on this mountain has been designated a National Treasure. Local temple lodgings run by priests still welcome worshippers today and serve traditional vegetarian cuisine shaped by the Shugendo faith.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '8ad99af4-1a76-408b-9bf8-6fb90fb3e3e0', page: 'about', url: 'dewa-sanzan-rebirth-cedars-yamagata'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link :to="{name: 'LuxuryTravel04'}" class="-gold">
                                    <span>Luxury</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan2/img02.jpg" alt="" class="_pc"><img src="../../assets/spirit_of_japan2/img02_sp.jpg" alt="" class="_sp"></div>
                <div class="txt">
                    <h3>Japan’s Largest Sacred Lake</h3>
                    <p>
                        Water is the elixir of life, and Japanese people have long worshipped water as a healer of illness and remover of impurities. As Japan’s largest body of water, 670-square-kilometer Lake Biwa holds a special place in the nation’s cultural heritage. Many of the shrines and temples dotted around the picturesque lake are devoted to the Buddha of the “pure land of water.”
                        An incredibly diverse range of flora and fauna live in or near this lake that has inspired countless artworks and impressive gardens. In modern times, Lake Biwa has become a cultural landscape offering an array of water-related activities and tourist attractions. Freshly caught fish and Omi wagyu beef are among the local delicacies that will have visitors licking their lips.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '84f5ab55-5d1d-43f2-a14c-2dc8d758197b', page: 'about', url: 'lake-biwa-shiga-kabata-hieizan-enryakuji-shirahige-shrine'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan2/img03.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Mountaintop where “the Savior of All Beings” Appeared</h3>
                    <p>
                        A spiritual symbol in Tottori Prefecture, Mt. Daisen is the oldest sacred mountain mentioned in Japanese literature and features in many local myths. The faith in Mt. Daisen’s divine power to protect animals stems from the belief that the bodhisattva of salvation appeared at the summit to help all living things. This was reinforced by a senior monk from Daisenji Temple who,
                        centuries ago, distributed protective charms and encouraged the grazing of cows and horses on the mountainside. The cultural and historical heritage sites on the mountain’s main routes showcase Daisenji’s former glory and the area’s history of worship. Guided hiking tours wind through a huge beech forest on Mt. Daisen, which is a wild bird sanctuary and a natural forest preserve.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '733c3947-2fcf-4869-84a9-08c5ff2be27f', page: 'about', url: 'daisen-livestock-market-jizo-tottori-izumo-daisenji-rishosui'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan2/img04.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Mountain that became One Huge Temple</h3>
                    <p>
                        Koyasan is a sacred mountain and the spiritual center of an esoteric school of Buddhism founded in the ninth century by Kukai, a monk and influential figure in the history of the religion in Japan. Kukai used this remote, lushly forested area in Wakayama Prefecture as a training ground for pilgrims. The entire mountain itself is essentially a huge temple,
                        as it is home to more than 100 such places of worship, including the impressive buildings of Kongobuji Temple. About half the temples on Koyasan operate <i>shukubo</i> lodgings that offer both pilgrims and visitors the mystical experience of staying overnight at a temple, eating vegetarian cuisine and attending the morning prayers.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: 'c7f07a0a-6670-4eb3-8280-babf3c559452', page: 'about', url: 'nyonin-koya-history-female-pilgrims-koyasan-trmple'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link :to="{name: 'LuxuryTravel01'}" class="-gold">
                                    <span>Luxury</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para -reverse">
                <div class="img"><img src="../../assets/spirit_of_japan2/img05.jpg" alt=""></div>
                <div class="txt">
                    <h3>The Cliff-Hanging Temple Hall and Nearby Hot Springs</h3>
                    <p>
                        Mt. Mitoku in Tottori Prefecture has been a training venue for monks since the ninth century. The area’s beautifully preserved temple buildings include Sanbutsuji Temple’s Nageiredo Hall, which was built almost 1,000 years ago and seemingly defies gravity as it clings to a sheer precipice. The steep, narrow path to the mountain summit is widely
                        considered the most dangerous route to any of Japan’s national treasures. Not far from this hub of spiritual worship is Misasa, a hot spring town where local legend says the waters will heal most ailments within a few days. Traditional <i>ryokan</i> inns that date back more than 150 years offer the perfect place to unwind further after a day soaking in the hot springs.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '88d07209-8204-4b75-bf82-d01e93644112', page: 'about', url: 'mitoku-misasa-hot-springs-climb-healing-tottori-nageiredo'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="basic-section">
            <div class="para">
                <div class="img"><img src="../../assets/spirit_of_japan2/img06.jpg" alt=""></div>
                <div class="txt">
                    <h3>The “Mountain of Spirits” and Performing Arts</h3>
                    <p>
                        Known for centuries as a “mountain of spirits,” Mt. Takao has a rich natural environment home to a wealth of animals and plants. Silk cultivation and production supported this area’s development during the Edo period (1603-1867), and silk merchants worshipped at a temple on Mt. Takao to seek divine protection of their industry.
                        Flourishing textile markets sparked the creation of local performing arts such as Hachioji <i>kuruma ningyo</i> puppetry and lion dances. Located about an hour from central Tokyo, Mt. Takao offers a spectacular view of Mt. Fuji and is said to be the world’s most heavily visited peak by climbers, ranging from beginners to experts.
                    </p>
                    <div class="btns">
                        <ul>
                            <li>
                                <router-link :to="{name: 'Popular', params: {id: '5f8d8270-67a5-4e47-b92d-cf590e3cf9bc', page: 'about', url: 'takao-mountain-mulberry-tokyo-hachioji-geisha'}}" class="-black">
                                    <span>Read More</span>
                                </router-link>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>
        <section class="section03">
            <div class="inner">
                <h2>Other Articles</h2>
                <ul>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan1'}">
                            <div class="img"><img src="../../assets/spirit_of_japan/other.jpg" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Discover Craftsmanship Unbowed by Time</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan3'}">
                            <div class="img"><img src="../../assets/spirit_of_japan3/other.png" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">Unlocking the Secrets of the Shogun, Samurai and Ninja</div>
                            </div>
                        </router-link>
                    </li>
                    <li>
                        <router-link :to="{name: 'SpiritOfJapan4'}">
                            <div class="img"><img src="../../assets/spirit_of_japan4/other.webp" alt=""></div>
                            <div class="text">
                                <div class="headline">Spirit of Japan</div>
                                <div class="title">The Journey of Japan's Original Landscapes Shaped by Water</div>
                            </div>
                        </router-link>
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script>
import ApiRouter from "../../components/Systems/ApiRouter";
import JsonLd from "@/components/JsonLd/JsonLd.vue";

export default {
    name: "PlanYourVisit",
    components: {JsonLd},
    mixins: [ApiRouter],
    data: function () {
        return {
            articles: [],
            activeTabMenu: 'half',
            jsonld: {
                "@context": "https://schema.org",
                "@type": "Article",
                "mainEntityOfPage": window.location.href,
                "headline": "Sacred sites where nature and faith blend into one",
                "description": "Japan Heritage ties together historical sites, shrines, temples, traditional arts, castles, armor," +
                    "and other cultural assets. Find photos, videos, tourist information, and more.",
                "image": [
                    require('../../assets/spirit_of_japan2/main01.jpg')
                ],
                "datePublished": "2024/11/11",
                "dateModified": "2024/11/13",
                "author": [{
                    "@type": "Organization",
                    "name": "Japan National Tourism Organization",
                    "url": "https://www.japan.travel/japan-heritage/"
                }]
            }
        }
    },
    created: function () {
        this.loadData();
        this.getQuery();
        document.title = 'Spirit Of Japan | Japan Heritage Official Site'
    },
    methods: {
        loadData: function () {
            window.axios.get(this.routes.articles)
                .then((response) => {
                    this.articles = response.data.data;
                });
        },
        getQuery: function () {
            const tab = this.$route.query.tab;

            switch (tab) {
                case 'day1':
                    this.activeTabMenu = 'day1';
                    break;
                case 'day2':
                    this.activeTabMenu = 'day2';
                    break;
                case 'day3':
                    this.activeTabMenu = 'day3';
                    break;
                default:
                    this.activeTabMenu = 'half';
                    break;
            }
        },
        articleCardHasClicked: function (id, hash, breadcrumb) {
            this.$router.push({name: 'Popular', params: {id: id, page: 'travelInfo', breadcrumb: breadcrumb}, hash: hash});
        }
    },
    watch: {
        $route() {
            this.getQuery();
        }
    }
}
</script>

<style src="./SpiritOfJapan2.scss" lang="scss" scoped />
